/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { JobContractDaysPeriodType, ScheduleDetailsDto, ScheduleTaskType, ScheduleType } from '@gh/builder-shared-data';
import { EMPTY_ID, WithVersion } from '@gh/common-shared-data';
import { cast, isNotNil } from '@gh/core-util';
import {
  calculateHierarchicalRelations,
  GANTT_WORKING_HOURS,
  ganttTaskRelationFromDto,
  genGanttTaskId,
} from '@gh/builder-shared-gantt-chart';
import { first, flatMap, get, isNil, map, mean, omit, sortBy } from 'lodash';
import { ScheduleGanttTask } from './schedule-gantt-details.model';

export type ScheduleCombinedGanttTask = ScheduleGanttTask;
export type ScheduleCombinedGanttDetails = WithVersion<{
  type: ScheduleType;
  name: string;
  tasks: ScheduleCombinedGanttTask[];
  jobContractConstructionPeriodIncludes: JobContractDaysPeriodType;
}>;

export const scheduleCombinedGanttDetailsFromDto = (type: ScheduleType,
                                                    schedules: ScheduleDetailsDto[]): ScheduleCombinedGanttDetails => {
  const sortedSchedules = sortBy(schedules, type === ScheduleType.Sales ? 'salesFolderName' : '');
  // @ts-ignore
  return {
    type,
    name: sortedSchedules.map((schedule) => schedule.jobNumber).join('-'),
    jobContractConstructionPeriodIncludes: get(
      first(sortedSchedules),
      'jobContractConstructionPeriodIncludes',
      JobContractDaysPeriodType.AllDays),
    tasks: sortedSchedules.map(newScheduleRootTask)
      .concat(flatMap(sortedSchedules, (schedule) => calculateHierarchicalRelations(schedule.tasks.map((task) => ({
        ...cast<ScheduleCombinedGanttTask>(omit(task, 'scheduleTaskId', 'parentTaskNumber', 'duration', 'resource')),
        duration: task.duration / GANTT_WORKING_HOURS,
        completionPct: task.completionPct || 0,
        physicalTaskId: task.scheduleTaskId!, // tslint:disable-line:no-non-null-assertion
        scheduleId: schedule.scheduleId,
        taskId: genGanttTaskId(schedule.scheduleId, task.taskNumber),
        parentTaskId: isNotNil(task.parentTaskNumber)
          ? genGanttTaskId(schedule.scheduleId, task.parentTaskNumber)
          : genGanttTaskId(schedule.scheduleId),
        opened: false,
        resource      : {
          name: task.resource,
          id  : task.supplierContactId || task.employeeContactId,
        },
        predecessors: map(
          task.predecessors,
          (predecessor) => ganttTaskRelationFromDto(schedule.scheduleId, predecessor)),
      }))))),
  };
};

const newScheduleRootTask = (schedule: ScheduleDetailsDto): ScheduleCombinedGanttTask =>
  ({
    physicalTaskId: EMPTY_ID,
    scheduleId: schedule.scheduleId,
    taskId: genGanttTaskId(schedule.scheduleId),
    type: ScheduleTaskType.Empty,
    hasChildrenExcludedFromGanttChart: false,
    taskNumber: <any>void 0,
    // tslint:disable-next-line:max-line-length
    name: `${schedule.type === ScheduleType.Sales ? schedule.salesFolderName : schedule.jobNumber} - ${schedule.customerName}`,
    isSlab: false,
    mayHaveResource: true,
    isInSupervisorSchedule: false,
    isExcludeFromGanttChart: false,
    isFromCriticalPath: false,
    isResourceInUse: false,
    startDate: schedule.startDate,
    endDate: schedule.endDate,
    baseStartDate: schedule.baseStartDate,
    baseEndDate: schedule.baseEndDate,
    duration: schedule.duration,
    completionPct: mean(schedule.tasks
      .filter((task) => isNil(task.parentTaskNumber))
      .filter((task) => !task?.isExcludeFromGanttChart)
      .map((task) => task.completionPct)) || 0,
    isContractStart: false,
    canBeDeleted: false,
    opened: false,
    hasChildren: true,
    canBeMarkedAsDeleted: false,
    markedAsDeleted: false,
  });
